import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useToast } from '../contexts/ToastContext';
import api from '../utils/api';
import {
  barDataset,
  buildAnalyticsChartOptions,
  contactsSectionTitle,
  formatSignedPercent,
  growthTone,
  lastN,
  lineDataset,
  monthLabel,
  prefersReducedMotion,
  rankedShares,
  seriesHasValues,
  shortWeekday,
  trimFutureMonths,
  trimLeadingEmpty,
} from '../utils/analyticsPresentation';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import '../styles/AdvancedUserAnalyticsPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

const LeafletPointsMap = lazy(() => import('../components/LeafletPointsMap'));

const NARROW_QUERY = '(max-width: 979px)';

function useNarrow() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(NARROW_QUERY).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(NARROW_QUERY);
    const onChange = (event) => setNarrow(event.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return narrow;
}

function Metric({ label, value, hint, tone, size, id }) {
  return (
    <div className="aua-kpi" id={id}>
      <div className="aua-kpi-label">{label}</div>
      <div className={`aua-kpi-value ${size === 'sm' ? 'aua-kpi-value-sm' : ''} ${tone || ''}`.trim()}>
        {value}
      </div>
      {hint ? <div className="aua-kpi-hint">{hint}</div> : null}
    </div>
  );
}

function Panel({ title, eyebrow, insight, actions, children }) {
  return (
    <section className="aua-panel">
      <div className="aua-panel-head">
        <div className="aua-panel-head-text">
          {eyebrow ? <p className="aua-eyebrow">{eyebrow}</p> : null}
          <h2 className="aua-panel-title">{title}</h2>
        </div>
        {actions || (insight ? <p className="aua-insight">{insight}</p> : null)}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="aua-empty">{children}</div>;
}

function RankedBars({ items }) {
  if (!items.length) return <Empty>No source data yet.</Empty>;
  const max = Math.max(...items.map((item) => item.value), 1);
  const peak = items[0]?.label;
  return (
    <ul className="aua-rows">
      {items.map((item) => (
        <li key={item.label} className="aua-row">
          <div className="aua-row-main">
            <span className="aua-row-label">{item.label}</span>
            <span className="aua-row-track" aria-hidden="true">
              <span
                className={`aua-row-fill${item.label === peak ? ' peak' : ''}`}
                style={{ width: `${Math.max(6, (item.value / max) * 100)}%` }}
              />
            </span>
          </div>
          <span className="aua-row-value">{item.percent}%</span>
        </li>
      ))}
    </ul>
  );
}

function GeoList({ title, rows, nameKey }) {
  const cleaned = (rows || []).filter((row) => row?.[nameKey] && row[nameKey] !== 'No data yet' && row.count > 0);
  return (
    <div>
      <h3 className="aua-geo-title">{title}</h3>
      {cleaned.length === 0 ? (
        <Empty>None yet.</Empty>
      ) : (
        <ul className="aua-rows">
          {cleaned.slice(0, 5).map((row) => (
            <li key={row[nameKey]} className="aua-row">
              <span className="aua-row-label">{row[nameKey]}</span>
              <span className="aua-row-value">{row.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="aua-status" role="status" aria-label="Loading analytics">
      <span className="aua-sr">Loading analytics</span>
      <div className="aua-kpi-grid">
        <div className="aua-skel aua-skel-kpi" />
        <div className="aua-skel aua-skel-kpi" />
        <div className="aua-skel aua-skel-kpi" />
        <div className="aua-skel aua-skel-kpi" />
      </div>
      <div className="aua-kpi-grid aua-kpi-grid-secondary">
        <div className="aua-skel aua-skel-kpi" />
        <div className="aua-skel aua-skel-kpi" />
        <div className="aua-skel aua-skel-kpi" />
      </div>
      <div className="aua-skel aua-skel-panel" />
      <div className="aua-skel aua-skel-panel" />
    </div>
  );
}

const AdvancedUserAnalyticsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const narrow = useNarrow();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();
  const { showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const [activityTab, setActivityTab] = useState('week');
  const [reloadKey, setReloadKey] = useState(0);
  const scoreInfoRef = useRef(null);
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get('/api/user-analytics/advanced-analytics')
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err.response?.data?.error || 'Failed to load analytics');
        showErrorRef.current('Failed to load analytics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (!showScoreInfo) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setShowScoreInfo(false);
    };
    const onPointer = (event) => {
      if (scoreInfoRef.current && !scoreInfoRef.current.contains(event.target)) {
        setShowScoreInfo(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [showScoreInfo]);

  useEffect(() => {
    if (loading || error) return undefined;
    if (location.hash !== '#cards-shared') return undefined;

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById('cards-shared');
      if (el) {
        el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 28) rafId = requestAnimationFrame(tryScroll);
    };
    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [loading, error, location.hash, location.pathname, data]);

  const reducedMotion = prefersReducedMotion();
  const monthWindow = narrow ? 6 : 12;

  const monthlyRows = useMemo(() => {
    const trimmed = trimLeadingEmpty(trimFutureMonths(data?.monthlyConnections));
    return lastN(trimmed, monthWindow);
  }, [data?.monthlyConnections, monthWindow]);

  const meetingRows = useMemo(() => {
    const trimmed = trimLeadingEmpty(trimFutureMonths(data?.meetingActivity), ['sent']);
    return lastN(trimmed, monthWindow);
  }, [data?.meetingActivity, monthWindow]);

  const chartOptions = useMemo(
    () => buildAnalyticsChartOptions({ reducedMotion, integerY: true, maxTicksLimit: narrow ? 6 : 8 }),
    [reducedMotion, narrow]
  );

  const monthlyChart = useMemo(() => {
    if (!seriesHasValues(monthlyRows)) return null;
    const labels = monthlyRows.map((row) => monthLabel(row, { compact: narrow }));
    const values = monthlyRows.map((row) => row.count);
    return {
      labels,
      datasets: [barDataset('New contacts', values)],
      rows: monthlyRows,
    };
  }, [monthlyRows, narrow]);

  const meetingChart = useMemo(() => {
    if (!seriesHasValues(meetingRows, ['sent'])) return null;
    return {
      labels: meetingRows.map((row) => monthLabel(row, { compact: narrow })),
      datasets: [lineDataset('Sent', meetingRows.map((row) => row.sent))],
    };
  }, [meetingRows, narrow]);

  const weeklyChart = useMemo(() => {
    const rows = data?.weeklyEngagement || [];
    if (!seriesHasValues(rows)) return null;
    return {
      labels: rows.map((row) => (narrow ? shortWeekday(row.label) : row.label)),
      datasets: [barDataset('Activity', rows.map((row) => row.count))],
    };
  }, [data?.weeklyEngagement, narrow]);

  const timeChart = useMemo(() => {
    const rows = data?.timeOfDayEngagement || [];
    if (!seriesHasValues(rows)) return null;
    return {
      labels: rows.map((row) => row.bucket),
      datasets: [barDataset('Engagement', rows.map((row) => row.count))],
    };
  }, [data?.timeOfDayEngagement]);

  const sources = useMemo(
    () => rankedShares(data?.connectionSources, { labelKey: 'source', valueKey: 'count' }),
    [data?.connectionSources]
  );

  const mapCenter = useMemo(() => {
    const pts = data?.geography?.mapPoints || [];
    if (!pts.length) return [20, 0];
    const lat = pts.reduce((sum, point) => sum + point.lat, 0) / pts.length;
    const lng = pts.reduce((sum, point) => sum + point.lng, 0) / pts.length;
    return [lat, lng];
  }, [data?.geography?.mapPoints]);

  const s = data?.summary || {};
  const engagement = data?.cardEngagement;
  const monthLabelText = engagement?.cardEngagementMonthLabel || '';
  const weeklyTotal = (data?.weeklyEngagement || []).reduce((sum, row) => sum + (row.count || 0), 0);
  const timeTotal = (data?.timeOfDayEngagement || []).reduce((sum, row) => sum + (row.count || 0), 0);
  const showWeekInsight = Boolean(data?.mostActiveDay) && weeklyTotal > 0;
  const showTimeInsight = Boolean(data?.bestTimeLabel) && timeTotal > 0;
  const contactsTitle = contactsSectionTitle(data?.topContactsSource);
  const growth = s.connectionGrowthRate;
  const hasMap = (data?.geography?.mapPoints || []).length > 0;

  const monthlyTooltip = useMemo(() => ({
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        ...chartOptions.plugins.tooltip,
        callbacks: {
          afterBody: (items) => {
            const index = items[0]?.dataIndex;
            const row = monthlyChart?.rows?.[index];
            if (row?.percentChangeVsPrevious == null) return '';
            return formatSignedPercent(row.percentChangeVsPrevious) + ' vs previous';
          },
        },
      },
    },
  }), [chartOptions, monthlyChart]);

  const meetingOptions = useMemo(() => ({
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        min: 0,
        ticks: {
          ...chartOptions.scales.y.ticks,
          stepSize: 1,
        },
      },
    },
  }), [chartOptions]);

  const weekPanel = (
    <Panel
      title="Most active day"
      insight={showWeekInsight ? <><strong>{data.mostActiveDay}</strong></> : null}
    >
      <div className="aua-panel-body">
        {weeklyChart ? (
          <div className="aua-chart">
            <Bar data={weeklyChart} options={chartOptions} />
          </div>
        ) : (
          <Empty>Not enough activity yet.</Empty>
        )}
      </div>
    </Panel>
  );

  const timePanel = (
    <Panel
      title="Best time of day"
      insight={showTimeInsight ? <><strong>{data.bestTimeLabel}</strong></> : null}
    >
      <div className="aua-panel-body">
        {timeChart ? (
          <div className="aua-chart">
            <Bar data={timeChart} options={chartOptions} />
          </div>
        ) : (
          <Empty>Not enough activity yet.</Empty>
        )}
      </div>
    </Panel>
  );

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />
      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Analytics"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />
        <div className={homeStyles.hubContent}>
          <div className="aua-page vx-app" aria-busy={loading}>
            <h1 className="aua-sr">Analytics</h1>

            {loading ? (
              <PageSkeleton />
            ) : error ? (
              <div className="aua-flash" role="alert">
                <span>{error}</span>
                <button
                  type="button"
                  className="aua-retry"
                  onClick={() => setReloadKey((key) => key + 1)}
                >
                  Try again
                </button>
              </div>
            ) : (
              <>
                <section className="aua-kpi-grid" aria-label="Summary">
                  <Metric label="Connections" value={s.totalConnections ?? 0} hint="All time" />
                  <Metric label="Unique views" value={s.uniqueProfileViews ?? 0} hint="All time" />
                  <div className="aua-anchor" id="cards-shared">
                    <Metric label="Cards shared" value={s.cardsShared ?? 0} hint="All time" />
                  </div>
                  <Metric label="Meetings sent" value={s.meetingRequestsSent ?? 0} hint="All time" />
                </section>

                <section className="aua-kpi-grid aua-kpi-grid-secondary" aria-label="Recent performance">
                  <Metric label="New contacts" value={s.contactsAddedThisMonth ?? 0} hint="Last 30 days" size="sm" />
                  <Metric
                    label="Growth"
                    value={formatSignedPercent(growth)}
                    hint="vs last month"
                    tone={growthTone(growth)}
                    size="sm"
                  />
                  <Metric
                    label="Conversion"
                    value={engagement?.conversionRatePercent != null ? `${engagement.conversionRatePercent}%` : '—'}
                    hint={monthLabelText || 'This month'}
                    size="sm"
                  />
                </section>

                <div className="aua-stack">
                  <Panel title="New contacts" eyebrow={narrow ? '6 months' : 'This year'}>
                    <div className="aua-panel-body">
                      {monthlyChart ? (
                        <div className="aua-chart">
                          <Bar data={monthlyChart} options={monthlyTooltip} />
                        </div>
                      ) : (
                        <Empty>No new contacts in this period.</Empty>
                      )}
                    </div>
                  </Panel>

                  <Panel title="Card engagement" eyebrow={monthLabelText}>
                    <div className="aua-panel-body">
                      {engagement ? (
                        <div className="aua-metrics">
                          <div className="aua-metric">
                            <span className="aua-metric-value">{engagement.uniqueCardVisitors ?? 0}</span>
                            <span className="aua-metric-label">Unique visitors</span>
                          </div>
                          <div className="aua-metric">
                            <span className="aua-metric-value">{engagement.cardViews ?? 0}</span>
                            <span className="aua-metric-label">Card views</span>
                          </div>
                          <div className="aua-metric">
                            <span className="aua-metric-value">{engagement.connectionsAdded ?? 0}</span>
                            <span className="aua-metric-label">Connections added</span>
                          </div>
                          <div className="aua-metric">
                            <span className="aua-metric-value">
                              {engagement.conversionRatePercent != null ? `${engagement.conversionRatePercent}%` : '—'}
                            </span>
                            <span className="aua-metric-label">Conversion</span>
                          </div>
                        </div>
                      ) : (
                        <Empty>No engagement this month.</Empty>
                      )}
                    </div>
                  </Panel>

                  {narrow ? (
                    <Panel
                      title="Activity"
                      actions={(
                        <div className="aua-tabs" role="tablist" aria-label="Activity">
                          <button
                            type="button"
                            role="tab"
                            className="aua-tab"
                            aria-selected={activityTab === 'week'}
                            onClick={() => setActivityTab('week')}
                          >
                            Week
                          </button>
                          <button
                            type="button"
                            role="tab"
                            className="aua-tab"
                            aria-selected={activityTab === 'time'}
                            onClick={() => setActivityTab('time')}
                          >
                            Time
                          </button>
                        </div>
                      )}
                    >
                      <div className="aua-panel-body" role="tabpanel">
                        {activityTab === 'week' ? (
                          weeklyChart ? (
                            <>
                              {showWeekInsight ? (
                                <p className="aua-insight aua-insight-lead">
                                  <strong>{data.mostActiveDay}</strong>
                                </p>
                              ) : null}
                              <div className="aua-chart">
                                <Bar data={weeklyChart} options={chartOptions} />
                              </div>
                            </>
                          ) : (
                            <Empty>Not enough activity yet.</Empty>
                          )
                        ) : timeChart ? (
                          <>
                            {showTimeInsight ? (
                              <p className="aua-insight aua-insight-lead">
                                <strong>{data.bestTimeLabel}</strong>
                              </p>
                            ) : null}
                            <div className="aua-chart">
                              <Bar data={timeChart} options={chartOptions} />
                            </div>
                          </>
                        ) : (
                          <Empty>Not enough activity yet.</Empty>
                        )}
                      </div>
                    </Panel>
                  ) : (
                    <div className="aua-split">
                      {weekPanel}
                      {timePanel}
                    </div>
                  )}

                  <Panel title="Meetings" eyebrow={narrow ? '6 months' : 'This year'}>
                    <div className="aua-panel-body">
                      {meetingChart ? (
                        <div className="aua-chart">
                          <Line data={meetingChart} options={meetingOptions} />
                        </div>
                      ) : (
                        <Empty>No meetings sent in this period.</Empty>
                      )}
                    </div>
                  </Panel>

                  <Panel title="Geography">
                    <div className="aua-panel-body">
                      {hasMap ? (
                        <>
                          <div className="aua-map-wrap">
                            <Suspense fallback={<div className="aua-map aua-map-loading">Loading map…</div>}>
                              <LeafletPointsMap
                                center={mapCenter}
                                zoom={2}
                                className="aua-map"
                                points={data.geography.mapPoints.map((point, index) => ({
                                  id: index,
                                  lat: point.lat,
                                  lng: point.lng,
                                  popup: point.label || 'Connection',
                                }))}
                              />
                            </Suspense>
                          </div>
                          <div className="aua-geo">
                            <GeoList title="Cities" rows={data.geography.topCities} nameKey="city" />
                            <GeoList title="Countries" rows={data.geography.topCountries} nameKey="country" />
                          </div>
                        </>
                      ) : (
                        <Empty>No locations yet.</Empty>
                      )}
                    </div>
                  </Panel>

                  <div className="aua-split aua-split-wide">
                    <Panel title="Sources">
                      <div className="aua-panel-body">
                        <RankedBars items={sources} />
                      </div>
                    </Panel>

                    <Panel title={contactsTitle}>
                      <div className="aua-panel-body">
                        {data?.topContacts?.length > 0 ? (
                          <ul className="aua-rows">
                            {data.topContacts.slice(0, 5).map((contact, index) => (
                              <li key={`${contact.email || contact.name || index}`} className="aua-contact">
                                <span className="aua-contact-rank">{index + 1}</span>
                                <div className="aua-contact-info">
                                  <span className="aua-contact-name">{contact.name || 'Unknown'}</span>
                                  {contact.email ? <span className="aua-contact-email">{contact.email}</span> : null}
                                </div>
                                {contact.views > 0 ? (
                                  <span className="aua-contact-views">{contact.views}</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Empty>No contacts yet.</Empty>
                        )}
                      </div>
                    </Panel>
                  </div>

                  <section className="aua-panel" ref={scoreInfoRef}>
                    <div className="aua-panel-head">
                      <div className="aua-score-head">
                        <div className="aua-panel-head-text">
                          <p className="aua-eyebrow">Score</p>
                          <h2 className="aua-panel-title">Networking</h2>
                        </div>
                        <button
                          type="button"
                          className="aua-info-btn"
                          onClick={() => setShowScoreInfo((open) => !open)}
                          aria-label="How the networking score is calculated"
                          aria-expanded={showScoreInfo}
                        >
                          i
                        </button>
                      </div>
                    </div>
                    {showScoreInfo ? (
                      <div className="aua-popover" role="dialog" aria-label="How the networking score is calculated">
                        <p className="aua-popover-title">How to raise it</p>
                        <ul className="aua-popover-list">
                          <li>Add more connections</li>
                          <li>Send meeting requests</li>
                          <li>Share your card</li>
                          <li>Stay active on Velox</li>
                        </ul>
                      </div>
                    ) : null}
                    <div className="aua-panel-body">
                      <div className="aua-score-row">
                        <div className="aua-score-value">
                          {data?.networkingScore ?? 0}
                          <span className="aua-score-max">/100</span>
                        </div>
                      </div>
                      <div
                        className="aua-meter"
                        role="progressbar"
                        aria-label="Networking score"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={data?.networkingScore ?? 0}
                      >
                        <span
                          className="aua-meter-fill"
                          style={{ width: `${Math.min(100, Math.max(0, data?.networkingScore ?? 0))}%` }}
                        />
                      </div>
                    </div>
                  </section>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedUserAnalyticsPage;
